<template>

  <v-container
      fluid
      :class="[
        'order-provider-compare-results',
        { 'order-provider-compare-results--headerless': hideHeader }
      ]">

    <!-- Title and close action -->
    <v-row v-if="!hideHeader" class="mt-1">
      <v-col cols="12" xs="12" sm="9" md="9" lg="9">
        <div class="title-text">
          {{ titleText }}
        </div>
        <div class="mt-6 pl-1 subtitle-text">
          <span>{{ subtitleText }}</span>
          <template v-if="showProviderStatus">
            <span class="pl-6 provider-status-label-text">--</span>
            <span class="pl-6 pr-2 provider-status-label-text">provider status:</span>
            <span>{{ compareResult.thirdparty_status }}</span>
          </template>
        </div>
      </v-col>
      <v-col cols="12" xs="12" sm="3" md="3" lg="3" align="right">
        <a class="pr-4" style="font-size:16px;" @click="closePreview">Close</a>
      </v-col>
    </v-row>

    <!-- Detailed Changes -->
    <v-container fluid class="mt-7 px-2 pb-4 page-content">

      <v-container class="detailed-changes-container">
        <!-- (order total change) -->
        <template v-if="orderTotalChangeInfo">
          <v-row>
            <v-col cols="12" class="change-title-text">Changed Order Total</v-col>
          </v-row>
          <v-row class="mt-4">
            <v-col cols="8" xs="8" sm="4" md="3" lg="3">
              <span class="product-field-label-text">current:</span>
              <span class="pl-2 current-total-text">{{ formatTotal(orderTotalChangeInfo.routeleaf) }}</span>
            </v-col>
            <v-col cols="2" xs="2" sm="2" md="1" lg="1">
              <v-icon color="grey darken-2">mdi-arrow-right-thin</v-icon>
            </v-col>
            <v-col cols="12" xs="12" sm="5" md="5" lg="5">
              <span class="product-field-label-text">changed:</span>
              <span class="pl-2 changed-total-text">{{ formatTotal(orderTotalChangeInfo.provider) }}</span>
            </v-col>
          </v-row>
        </template>

        <!-- (ordered items added) -->
        <template v-if="orderedItemsAddedInfo">
          <v-row class="mt-8 mb-0">
            <v-col cols="12" class="pb-2 change-title-text">Added Ordered Items</v-col>
          </v-row>
          <v-container class="mt-4 changed-item-container item-added" v-for="(item, index) in orderedItemsAddedInfo" :key="`added-${index}`">
            <v-row class="mt-0">
              <v-col cols="3" align="center" class="pt-0 pb-1 product-field-label-text">Product ID</v-col>
              <v-col cols="9" class="pt-0 pb-1 product-field-label-text">Product Label</v-col>
            </v-row>
            <v-row class="mt-2 pb-1">
              <v-col cols="3">
                <v-icon color="green" class="pr-3 changed-item-icon">mdi-plus</v-icon>
                {{ item.product_id }}
              </v-col>
              <v-col cols="9">
                {{ item.product_label }}
              </v-col>
            </v-row>
          </v-container>
        </template>

        <!-- (ordered items removed) -->
        <template v-if="orderedItemsRemovedInfo">
          <v-row class="mt-8 mb-0">
            <v-col cols="12" class="pb-2 change-title-text">Removed Ordered Items</v-col>
          </v-row>
          <v-container class="mt-4 changed-item-container item-removed" v-for="(item, index) in orderedItemsRemovedInfo" :key="`removed-${index}`">
            <v-row class="mt-0">
              <v-col cols="3" align="center" class="pt-0 pb-1 product-field-label-text">Product ID</v-col>
              <v-col cols="9" class="pt-0 pb-1 product-field-label-text">Product Label</v-col>
            </v-row>
            <v-row class="mt-2 pb-1">
              <v-col cols="3">
                <v-icon color="red" class="pr-3 changed-item-icon">mdi-minus</v-icon>
                {{ item.product_id }}
              </v-col>
              <v-col cols="9">
                {{ item.product_label }}
              </v-col>
            </v-row>
          </v-container>
        </template>

        <!-- (ordered item details changed) -->
        <template v-if="itemChangesInfo">
          <v-row class="mt-8 mb-0">
            <v-col cols="12" class="pb-2 change-title-text">Changed Ordered Item Details</v-col>
          </v-row>
          <v-container class="mt-4 changed-item-container" v-for="(item, index) in itemChangesInfo" :key="`item-change-${index}`">
            <v-row>
              <v-col cols="3" class="pt-2 pb-1 product-field-label-text">Product ID</v-col>
              <v-col cols="9" class="pt-2 pb-1 product-field-label-text">Product Label</v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="3">{{ item.product_id }}</v-col>
              <v-col cols="9">{{ item.product_label }}</v-col>
            </v-row>
            <v-row class="mt-3">
              <v-col cols="3" class="pb-1 product-field-label-text">Field</v-col>
              <v-col cols="4" class="pb-1 product-field-label-text">Current</v-col>
              <v-col cols="1" class="pb-1"></v-col>
              <v-col cols="4" class="pb-1 product-field-label-text">Changed</v-col>
            </v-row>
            <v-row
                class="mt-0"
                v-for="(change, field) in item.changes"
                :key="`item-change-${index}-${field}`">
              <v-col cols="3" class="product-field-label-text">{{ formatItemFieldLabel(field) }}</v-col>
              <v-col cols="4" class="current-total-text item-detail-value">{{ formatItemChangeValue(field, change.routeleaf) }}</v-col>
              <v-col cols="1">
                <v-icon color="grey darken-2">mdi-arrow-right-thin</v-icon>
              </v-col>
              <v-col cols="4" class="changed-total-text item-detail-value">{{ formatItemChangeValue(field, change.provider) }}</v-col>
            </v-row>
          </v-container>
        </template>

        <!-- (batch associations changed) -->
        <template v-if="batchAssociationsChangeInfo">
          <v-row class="mt-8 mb-0">
            <v-col cols="12" class="pb-2 change-title-text">Changed Batches</v-col>
          </v-row>
          <v-container class="mt-4 changed-item-container" v-for="(item, index) in batchAssociationsChangeInfo" :key="`batch-change-${index}`">
            <v-row>
              <v-col cols="3" align="left" class="pt-2 pb-1 product-field-label-text">Product ID</v-col>
              <v-col cols="9" class="pt-2 pb-1 product-field-label-text">Product Label</v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="3">
                {{ item.product_id }}
              </v-col>
              <v-col cols="9">
                {{ item.product_label }}
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="12">
                <span class="pr-2 product-field-label-text">Batch:</span>
              </v-col>
            </v-row>
            <v-row class="mt-0">
              <v-col cols="5" xs="5" sm="5" md="5" lg="5">
                <span class="current-total-text">{{ item.batch_number }}</span>
              </v-col>
              <v-col cols="2" xs="1" sm="2" md="2" lg="2">
                <v-icon color="grey darken-2">mdi-arrow-right-thin</v-icon>
              </v-col>
              <v-col cols="5" xs="5" sm="5" md="5" lg="5">
                <span class="changed-total-text">{{ item.provider_batch_number }}</span>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </v-container>

    </v-container> <!-- END page-content -->

    <!-- Action Buttons -->
    <!-- <div style="text-align:center;height:80px;">
      <template>
      </template>
    </div> -->

  </v-container>

</template>

<script>
export default {
  name: 'OrderProviderCompareResults',

  props: {
    compareResult: {
      type: Object,
      required: true
    },
    showProviderStatus: {
      type: Boolean,
      default: false
    },
    hideHeader: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
    }
  },

  computed: {
    providerName () {
      return (this.compareResult.provider_alias)
        ? this.$root.providerAliasToName(this.compareResult.provider_alias)
        : null
    },
    titleText () {
      return (this.providerName)
        ? `Current Changes from ${this.providerName}`
        : 'Current Changes from Provider'
    },
    subtitleText () {
      return `Order: ${this.compareResult.order_number}`
    },
    diff () {
      return this.compareResult?.diff || {}
    },
    // --------------------------------- Possible changes
    orderTotalChangeInfo () {
      return this.diff.order_total
    },
    orderedItemsAddedInfo () {
      return this.diff.ordered_items?.provider
    },
    orderedItemsRemovedInfo () {
      return this.diff.ordered_items?.routeleaf
    },
    itemChangesInfo () {
      return this.diff.item_changes
    },
    batchAssociationsChangeInfo () {
      return this.diff.batch_associations
    }
  },

  methods: {
    // Emit close action
    closePreview () {
      this.$emit('close')
    },

    // Display formatted total
    formatTotal (total) {
      const totalAsNum = total || 0
      return `$${Number(totalAsNum).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },

    // Display a readable label for an ordered item field
    formatItemFieldLabel (field) {
      const labels = {
        product_label: 'Product Label',
        product_strain: 'Product Strain',
        product_sku: 'Product SKU',
        batch_date: 'Batch Date',
        unit_price: 'Unit Price',
        sale_price: 'Sale Price',
        is_sale: 'On Sale',
        quantity: 'Quantity',
        item_total: 'Item Total'
      }
      return labels[field] || field.replace(/_/g, ' ')
    },

    // Display a changed ordered item value according to its field type
    formatItemChangeValue (field, value) {
      if (value == null || value === '') return 'Not set'
      if (['unit_price', 'sale_price', 'item_total'].includes(field)) return this.formatTotal(value)
      if (typeof value === 'boolean') return value ? 'Yes' : 'No'
      return value
    }
  }
}
</script>

<style>
  .order-provider-compare-results .title-text {
    font-size: 1.5rem;
  }

  .order-provider-compare-results .subtitle-text {
    color: #90A4AE;
    font-size: 1.1rem;
    /* font-style: italic; */
  }

  .order-provider-compare-results .provider-status-label-text {
    color: #727272;
    font-style: italic;
  }

  .order-provider-compare-results .action-button {
    height: 52px !important;
    width: 140px;
  }

  .order-provider-compare-results .detailed-changes-container {
    font-size: 1.0rem;
    text-align: left;
    max-height: 580px;
    overflow-y: scroll;
  }

  .order-provider-compare-results--headerless .detailed-changes-container {
    max-height: none;
    overflow-y: visible;
  }

  .order-provider-compare-results .change-title-text {
    text-transform: uppercase !important;
    letter-spacing: 1.14px;
    color: #90A4AE;
  }

  .order-provider-compare-results .product-field-label-text {
    color: #525252;
    font-size: 0.8rem;
  }

  .order-provider-compare-results .changed-item-icon {
    font-size: 22px;
  }

  .order-provider-compare-results .current-total-text {
    color: #F44336;
    font-size: 1.04rem;
  }

  .order-provider-compare-results .changed-total-text {
    color: #4CAF50;
    font-size: 1.08rem;
  }

  .order-provider-compare-results .item-detail-value {
    overflow-wrap: anywhere;
  }

  .order-provider-compare-results .changed-item-container {
    border: 1px solid #393939;
    border-radius: 6px;
  }

  .order-provider-compare-results .changed-item-container.item-added {
    border: 1px solid #4CAF50;
  }

  .order-provider-compare-results .changed-item-container.item-removed {
    border: 1px solid #F44336;
  }

  @media screen and (max-width: 600px) {
    .order-provider-compare-results .detailed-changes-container {
      max-height: 100% !important;
    }
  }
</style>
